/**
 * Mechanic and service duration performance (#2707).
 * Ratios from the API are decimals (billable/actual, planned/actual).
 */

import {
  Alert,
  Box,
  CircularProgress,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import type {
  DurationPerformanceMetrics,
  MechanicDurationPerformanceRow,
  ServiceDurationPerformanceRow,
  WorkOrderDurationPerformanceReport,
} from '@/hooks/useReports';
import { formatHoursOrDash } from '@/utils/actualTime';

export interface DurationPerformanceQueryState {
  data?: WorkOrderDurationPerformanceReport;
  isLoading: boolean;
  isError: boolean;
}

interface DurationPerformanceSectionProps {
  query: DurationPerformanceQueryState;
}

export function formatDurationRatioPercent(ratio: number | null): string {
  if (ratio == null || !Number.isFinite(ratio)) return '—';
  const percent = Math.round(ratio * 1000) / 10;
  const label = Number.isInteger(percent) ? String(percent) : percent.toFixed(1);
  return `${label}%`;
}

function metricCells(metrics: DurationPerformanceMetrics) {
  return (
    <>
      <TableCell align="right">{formatHoursOrDash(metrics.plannedHours)}</TableCell>
      <TableCell align="right">{formatHoursOrDash(metrics.billableHours)}</TableCell>
      <TableCell align="right">{formatHoursOrDash(metrics.actualHours)}</TableCell>
      <TableCell align="right">{formatDurationRatioPercent(metrics.efficiency)}</TableCell>
      <TableCell align="right">{formatDurationRatioPercent(metrics.planningAccuracy)}</TableCell>
    </>
  );
}

function DurationMetricsTable({
  testId,
  caption,
  nameHeader,
  emptyLabel,
  rows,
  unassignedLabel,
}: {
  testId: string;
  caption: string;
  nameHeader: string;
  emptyLabel: string;
  rows: Array<
    DurationPerformanceMetrics & {
      id: string | null;
      name: string | null;
    }
  >;
  unassignedLabel: string;
}) {
  const { t } = useTranslation('reports');

  return (
    <TableContainer sx={{ overflowX: 'auto' }} data-testid={testId}>
      <Table size="small" aria-label={caption}>
        <TableHead>
          <TableRow>
            <TableCell>{nameHeader}</TableCell>
            <TableCell align="right">{t('plannedHours')}</TableCell>
            <TableCell align="right">{t('billableHours')}</TableCell>
            <TableCell align="right">{t('actualHours')}</TableCell>
            <TableCell align="right">{t('efficiencyPercent')}</TableCell>
            <TableCell align="right">{t('planningAccuracyPercent')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={6}>
                <Typography color="text.secondary">{emptyLabel}</Typography>
              </TableCell>
            </TableRow>
          ) : (
            rows.map((row, index) => (
              <TableRow key={`${row.id ?? 'unassigned'}-${index}`} hover>
                <TableCell>
                  <Typography variant="body2">{row.name ?? unassignedLabel}</Typography>
                </TableCell>
                {metricCells(row)}
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function toMechanicRows(rows: MechanicDurationPerformanceRow[]) {
  return rows.map((row) => ({
    id: row.mechanicId,
    name: row.name,
    plannedHours: row.plannedHours,
    billableHours: row.billableHours,
    actualHours: row.actualHours,
    efficiency: row.efficiency,
    planningAccuracy: row.planningAccuracy,
  }));
}

function toServiceRows(rows: ServiceDurationPerformanceRow[]) {
  return rows.map((row) => ({
    id: row.serviceId,
    name: row.name,
    plannedHours: row.plannedHours,
    billableHours: row.billableHours,
    actualHours: row.actualHours,
    efficiency: row.efficiency,
    planningAccuracy: row.planningAccuracy,
  }));
}

export function DurationPerformanceSection({ query }: DurationPerformanceSectionProps) {
  const { t } = useTranslation('reports');
  const { t: tc } = useTranslation('common');
  const mechanics = query.data?.mechanics ?? [];
  const services = query.data?.services ?? [];
  const empty = mechanics.length === 0 && services.length === 0;

  return (
    <Grid size={{ xs: 12 }} data-testid="duration-performance">
      <Paper elevation={0} sx={{ p: { xs: 2, md: 3 }, border: 1, borderColor: 'divider' }}>
        <Typography variant="h6" fontWeight={600} gutterBottom>
          {t('durationPerformance')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('durationPerformanceHint')}
        </Typography>

        {query.isLoading ? (
          <Box
            sx={{ display: 'flex', justifyContent: 'center', py: 4 }}
            data-testid="duration-performance-loading"
            role="status"
            aria-label={tc('loading')}
          >
            <CircularProgress size={32} />
          </Box>
        ) : query.isError ? (
          <Alert severity="error" data-testid="duration-performance-error">
            {t('durationPerformanceError')}
          </Alert>
        ) : empty ? (
          <Typography color="text.secondary" data-testid="duration-performance-empty">
            {t('durationPerformanceEmpty')}
          </Typography>
        ) : (
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, lg: 6 }}>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                {t('mechanicsDuration')}
              </Typography>
              <DurationMetricsTable
                testId="duration-performance-mechanics"
                caption={t('mechanicsDuration')}
                nameHeader={t('mechanic')}
                emptyLabel={tc('noData')}
                unassignedLabel={t('durationPerformanceUnassigned')}
                rows={toMechanicRows(mechanics)}
              />
            </Grid>
            <Grid size={{ xs: 12, lg: 6 }}>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                {t('servicesDuration')}
              </Typography>
              <DurationMetricsTable
                testId="duration-performance-services"
                caption={t('servicesDuration')}
                nameHeader={t('serviceType')}
                emptyLabel={tc('noData')}
                unassignedLabel={t('durationPerformanceUnassigned')}
                rows={toServiceRows(services)}
              />
            </Grid>
          </Grid>
        )}
      </Paper>
    </Grid>
  );
}
